import { TextLink } from '@petsmart-ui/sparky';
import { type CSSProperties, type MouseEvent, useId } from 'react';

import { trackNavigationHeaderClick, useServicesLinkClick } from '../../../../../../shared';
import type { NavigationGroupModel } from '../../../../../../types';
import { resolveColumnCount } from './links-columns.utils';
import './links-columns.scss';

type LinksColumnsProps = {
    columns: NavigationGroupModel[][];
    desktopColumnCount?: number;
    selectionPath: string[];
};

export function LinksColumns({ columns, desktopColumnCount, selectionPath }: Readonly<LinksColumnsProps>) {
    const columnCount = resolveColumnCount(desktopColumnCount, columns);
    const style = { '--links-columns-count': columnCount } as CSSProperties;

    return (
        <div className="links-columns" style={style}>
            {Array.from({ length: columnCount }, (_, columnIndex) => (
                <div key={columnIndex} className="links-columns__column">
                    {(columns[columnIndex] ?? []).map((group, groupIndex) => (
                        <LinksColumnsGroup
                            key={`${columnIndex}-${groupIndex}-${group.title}`}
                            group={group}
                            selectionPath={selectionPath}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
}

type LinksColumnsGroupProps = {
    group: NavigationGroupModel;
    selectionPath: string[];
};

function LinksColumnsGroup({ group, selectionPath }: Readonly<LinksColumnsGroupProps>) {
    const titleId = useId();
    const groupPath = [...selectionPath, group.title];
    const overviewLink = group.overviewLink;
    const handleServicesLinkClick = useServicesLinkClick();

    function handleLinkClick(event: MouseEvent, href: string, selection: string[], openInNewTab?: boolean) {
        trackNavigationHeaderClick({
            selection,
            linkUrl: href,
            navSection: 'main nav',
        });
        handleServicesLinkClick({ event, link: href, shouldOpenInNewTab: openInNewTab });
    }

    return (
        <section className="links-columns__group">
            <h3 id={titleId} className="links-columns__group-title sparky-u-theme-typography-body-bold">
                {group.titleHref ? (
                    <TextLink
                        href={group.titleHref}
                        onClick={event => {
                            if (group.titleHref) handleLinkClick(event, group.titleHref, groupPath);
                        }}
                    >
                        {group.title}
                    </TextLink>
                ) : (
                    group.title
                )}
            </h3>
            <ul aria-labelledby={titleId} className="links-columns__group-links">
                {group.links.map(link => (
                    <li key={`${link.href}::${link.label}`} className="links-columns__group-link-item">
                        <TextLink
                            href={link.href}
                            variant="secondary"
                            rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
                            target={link.openInNewTab ? '_blank' : undefined}
                            onClick={event =>
                                handleLinkClick(event, link.href, [...groupPath, link.label], link.openInNewTab)
                            }
                        >
                            {link.label}
                        </TextLink>
                    </li>
                ))}
                {overviewLink && (
                    <li className="links-columns__group-link-item">
                        <TextLink
                            href={overviewLink.href}
                            rel={group.overviewLink?.openInNewTab ? 'noopener noreferrer' : undefined}
                            target={group.overviewLink?.openInNewTab ? '_blank' : undefined}
                            onClick={event =>
                                handleLinkClick(
                                    event,
                                    overviewLink.href,
                                    [...groupPath, overviewLink.label],
                                    overviewLink.openInNewTab
                                )
                            }
                        >
                            {overviewLink.label}
                        </TextLink>
                    </li>
                )}
            </ul>
        </section>
    );
}
